import { useState } from 'react';
import { ShieldCheck } from 'lucide-react';
import CheckoutAlert from '../CheckoutAlert';
import { CheckRow, buttonClass } from '../CheckoutControls';
import { formatCents, type CheckoutPlan, type OrderSummary } from '@/lib/checkout/pricing';
import type { AgreementDraft } from '@/lib/checkout/types';

export type PurchaseStatus = 'idle' | 'submitting' | 'authenticating' | 'provisioning_failed';

interface CompletePurchaseStepProps {
  plan: CheckoutPlan;
  order: OrderSummary;
  value: AgreementDraft;
  onChange: (patch: Partial<AgreementDraft>) => void;
  onBack: () => void;
  onSubmit: () => void;
  onConfirmAuthentication: () => void;
  status: PurchaseStatus;
}

/** Step 5 — itemised total, required agreement, marketing opt-in and purchase. */
export default function CompletePurchaseStep({
  plan,
  order,
  value,
  onChange,
  onBack,
  onSubmit,
  onConfirmAuthentication,
  status,
}: CompletePurchaseStepProps) {
  const [showTermsError, setShowTermsError] = useState(false);
  const submitting = status === 'submitting';
  // Only an idle flow may start a charge: `authenticating` is waiting on the 3DS
  // challenge and `provisioning_failed` has already been paid for.
  const canSubmit = status === 'idle';

  return (
    <>
      {status === 'provisioning_failed' ? (
        <CheckoutAlert
          tone="warn"
          title="We’re finishing setting up your account"
          actions={
            <a href="mailto:support@worldvia.com" className={buttonClass('smOutline')}>
              Contact support
            </a>
          }
        >
          Your payment went through, so there’s nothing more to pay. One of the setup steps didn’t
          complete — our team has been alerted and will email you at the address you gave us as soon
          as your account is ready. You don’t need to check out again.
        </CheckoutAlert>
      ) : null}

      {status === 'authenticating' ? (
        <CheckoutAlert
          tone="info"
          title="Your bank needs to verify this payment"
          actions={
            <button type="button" onClick={onConfirmAuthentication} className={buttonClass('sm')}>
              Continue to verification
            </button>
          }
        >
          Your card issuer requires 3D Secure authentication. You’ll be handed to your bank to
          approve the payment, then returned here — nothing has been charged yet.
        </CheckoutAlert>
      ) : null}

      <h2 className="m-0 mb-1.5 text-xl font-bold text-[#4b0a5b] sm:text-[21px]">
        Complete Membership Purchase
      </h2>
      <p className="mb-5 text-[13px] leading-relaxed text-[#7a6a8c]">
        Review your order, accept the membership terms, and we’ll activate your account.
      </p>

      <div className="mb-5 rounded-2xl border border-[#ece6f0] bg-[#fbfafc] p-4">
        <h3 className="m-0 mb-2.5 text-[12.5px] font-bold text-[#3d1751]">Order summary</h3>
        <dl>
          {order.lines.map((line) => (
            <div key={line.id} className="flex justify-between gap-3 py-1 text-[12.5px]">
              <dt className="text-[#6b5b7d]">{line.label}</dt>
              <dd className={`font-semibold ${line.cents < 0 ? 'text-[#0f7a63]' : 'text-[#3d1751]'}`}>
                {formatCents(line.cents)}
              </dd>
            </div>
          ))}
          <div className="mt-2.5 flex justify-between border-t border-[#ece6f0] pt-2.5">
            <dt className="text-sm font-bold text-[#3d1751]">Due now</dt>
            <dd className="text-sm font-bold text-[#3d1751]">{formatCents(order.dueNowCents)}</dd>
          </div>
        </dl>
        <p className="mt-2 text-[11px] leading-snug text-[#7a6a8c]">
          Your {plan.name} subscription then renews at {formatCents(order.monthlyCents)} per month.
        </p>
      </div>

      <CheckRow
        id="agreement-terms"
        checked={value.termsAccepted}
        invalid={showTermsError && !value.termsAccepted}
        describedBy={showTermsError && !value.termsAccepted ? 'agreement-terms-error' : undefined}
        onChange={(checked) => {
          onChange({ termsAccepted: checked });
          if (checked) setShowTermsError(false);
        }}
      >
        I have read and agree to the Membership Terms of Service Agreement.{' '}
        <a
          href="/landing#membership-terms"
          target="_blank"
          rel="noreferrer"
          className="font-medium text-[#b82f74] underline"
        >
          (View full agreement)
        </a>
      </CheckRow>

      {showTermsError && !value.termsAccepted ? (
        <div id="agreement-terms-error">
          <CheckoutAlert
            tone="error"
            title="You must accept the Membership Terms of Service to continue."
            compact
          >
            Review and check the box above to complete your purchase.
          </CheckoutAlert>
        </div>
      ) : null}

      <CheckRow
        id="agreement-marketing"
        checked={value.marketingOptIn}
        onChange={(checked) => onChange({ marketingOptIn: checked })}
      >
        Opt in to marketing emails — don’t miss the latest travel news and promotions from WorldVia!
      </CheckRow>

      {!value.marketingOptIn ? (
        <CheckoutAlert tone="warn" title="You’ll miss out." compact live={false}>
          Without opting in you won’t receive travel news, supplier promotions or member event
          invitations. You can still complete your purchase.
        </CheckoutAlert>
      ) : null}

      <p className="mb-4 flex items-center gap-1.5 text-[11.5px] text-[#7a6a8c]">
        <ShieldCheck aria-hidden="true" className="h-3.5 w-3.5" />
        Your card is charged {formatCents(order.dueNowCents)} today.
      </p>

      <div className="mt-4 flex gap-2">
        <button
          type="button"
          onClick={onBack}
          disabled={submitting}
          className={buttonClass('outline')}
        >
          Back
        </button>
        <button
          type="button"
          aria-disabled={!value.termsAccepted || !canSubmit}
          onClick={() => {
            if (!value.termsAccepted) {
              setShowTermsError(true);
              return;
            }
            if (!canSubmit) return;
            onSubmit();
          }}
          className={`${buttonClass('primary')} ${
            !value.termsAccepted || !canSubmit ? 'cursor-not-allowed opacity-60' : ''
          }`}
        >
          {submitting ? 'Processing payment…' : 'Complete Purchase'}
        </button>
      </div>
    </>
  );
}
